// Sends a label master PDF to Drive (list 2, 2026-10-25). The PDF waits on the iPad like a photo and
// goes on signal (survey-sync saveDrivePdf). The server replaces the file in place (functions/src/drive.ts).
// The card watches the send's driveFiles record and shows where it stands.

import { masterSendName, type DriveFileKind } from "./drive-folders";
import { callServer, loadDriveMasters, loadDriveSettings, watchDriveFile } from "./firebase";
import { outboxPath } from "./drive-folders";
import type { MasterRecord, PcState } from "./print-card";
import { saveDrivePdf } from "./survey-sync";

const watches = new Map<string, () => void>();

/** On open: is Drive on, and where each master stands. Never stops the card: offline it says less. */
export async function loadMasterState(s: PcState, company: string, site: string, repaint: () => void): Promise<void> {
  try {
    const d = await loadDriveSettings(company);
    s.driveOn = Boolean(d?.connected);
  } catch {
    s.driveOn = false;
  }
  try {
    const got = await loadDriveMasters(company, site);
    // A send made while this loaded wins over what the cloud had.
    const had = s.masters;
    s.masters = got;
    if (had) for (const [k, v] of had) if (!got.has(k) || got.get(k)!.sendName < v.sendName) got.set(k, v);
  } catch {
    s.masters = s.masters ?? null;
  }
  repaint();
}

/** Queues one master and watches it. Drive off: nothing is sent; the file stays on the card to download. */
export async function sendMaster(
  s: PcState,
  o: { company: string; site: string; kind: DriveFileKind; driveName: string; bytes: Uint8Array; badgeId: string; byName: string },
  repaint: () => void,
): Promise<void> {
  if (!s.driveOn) return;
  const sendName = masterSendName(o.driveName, Date.now());
  const rec: MasterRecord = { driveName: o.driveName, state: "waiting", savedAt: "", url: "", folderPath: "", folderUrl: "", reason: "", sendName };
  (s.masters ??= new Map()).set(o.driveName, rec);
  await saveDrivePdf({
    path: outboxPath(o.company, o.site, o.kind, sendName),
    company: o.company,
    siteNumber: o.site,
    assetId: "",
    contentType: "application/pdf",
    blob: new Blob([o.bytes as Uint8Array<ArrayBuffer>], { type: "application/pdf" }),
    takenAt: new Date().toISOString(),
    meta: { badgeId: o.badgeId, byName: o.byName },
  });
  watch(s, o.company, o.site, rec, repaint);
}

function watch(s: PcState, company: string, site: string, rec: MasterRecord, repaint: () => void): void {
  watches.get(rec.driveName)?.();
  watches.set(
    rec.driveName,
    watchDriveFile(company, site, rec.sendName, (r) => {
      if (!r || r.state === "retrying" || s.masters?.get(rec.driveName)?.sendName !== rec.sendName) return;
      Object.assign(rec, { state: r.state === "saved" ? "saved" : "failed", folderPath: r.folderPath, folderUrl: r.folderUrl, reason: r.reason, url: r.url ?? "", savedAt: r.savedAt ?? new Date().toISOString() });
      repaint();
    }),
  );
}

/** Try Again on a master that didn't reach Drive. The file is still in the outbox. */
export async function retryMaster(s: PcState, site: string, driveName: string, repaint: () => void): Promise<void> {
  const rec = s.masters?.get(driveName);
  if (!rec) return;
  Object.assign(rec, { state: "waiting", reason: "" });
  repaint();
  try {
    const r = await callServer<{ state: string; reason: string; folderPath: string; folderUrl: string }>("retryDriveFile", { siteNumber: site, fileName: rec.sendName });
    Object.assign(rec, { state: r.state === "saved" ? "saved" : "failed", folderPath: r.folderPath, folderUrl: r.folderUrl, reason: r.reason, savedAt: new Date().toISOString() });
  } catch (e) {
    Object.assign(rec, { state: "failed", reason: e instanceof Error ? e.message : String(e) });
  }
  repaint();
}

/** Leaving the card stops the watches. */
export function stopMasterWatches(): void {
  for (const u of watches.values()) u();
  watches.clear();
}
